'use client';

import React, { useState } from 'react';
import {
  ColorInputField,
  ColorPicker,
  ValueInputField,
} from '@crabs-ai/ds-react';

type ColorPickerFieldProps = {
  /** 현재 색상 값 (HEX) */
  value: string;
  /** 색상 변경 핸들러 (드래그 중 실시간 프리뷰 포함) */
  onChange: (color: string) => void;
  /** 인터랙션 완료 시 호출 (드래그 드롭, 프리셋 클릭, 입력 확정) */
  onChangeEnd?: (color: string) => void;
  /** 접근성 라벨 */
  accessibilityLabel: string;
  /** Mixed 상태 여부 (여러 아이템 선택 시 서로 다른 색상) */
  hasMixedValue?: boolean;
  /** Mixed value일 때 표시할 라벨 (기본값: 'Mixed') */
  mixedLabel?: string;
  /** 추가 className */
  className?: string;
  /** 불투명도 (0-1, 외부 제어) */
  opacity?: number;
  /** 불투명도 변경 핸들러 (0-1, 드래그 중 실시간 프리뷰 포함) */
  onOpacityChange?: (opacity: number) => void;
  /** opacity 인터랙션 완료 시 호출 */
  onOpacityChangeEnd?: (opacity: number) => void;
  /** 불투명도 Mixed 상태 여부 */
  isOpacityMixed?: boolean;
};

export const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  value,
  onChange,
  onChangeEnd,
  accessibilityLabel,
  hasMixedValue = false,
  mixedLabel = 'Mixed',
  className,
  opacity: controlledOpacity,
  onOpacityChange: controlledOnOpacityChange,
  onOpacityChangeEnd,
  isOpacityMixed = false,
}) => {
  const pickerValue = value === 'transparent' ? null : value;
  const [internalOpacity, setInternalOpacity] = useState(1);

  const opacity = controlledOpacity ?? internalOpacity;
  const onOpacityChange = controlledOnOpacityChange ?? setInternalOpacity;
  const opacityPercent = Math.round(opacity * 100);
  const isNone = pickerValue == null && !hasMixedValue;

  return (
    <div className={className ?? 'flex items-center gap-4xs'}>
      <div className="min-w-0 flex-1">
        <ColorInputField
          type={isNone ? 'none' : 'default'}
          value={(pickerValue ?? '').toUpperCase()}
          accessibilityLabel={accessibilityLabel}
          hasMixedValue={hasMixedValue}
          mixedLabel={mixedLabel}
          dropdownContent={
            <ColorPicker
              value={pickerValue}
              onChange={(nextValue) => {
                onChange(nextValue ?? 'transparent');
              }}
              onChangeEnd={(nextValue) => {
                onChangeEnd?.(nextValue ?? 'transparent');
              }}
              opacity={opacity}
              onOpacityChange={onOpacityChange}
              onOpacityChangeEnd={onOpacityChangeEnd}
            >
              <ColorPicker.ModeSwitch />
              <ColorPicker.Area />
              <ColorPicker.Input />
              <ColorPicker.Presets />
            </ColorPicker>
          }
        />
      </div>
      <div className="w-[96px] shrink-0">
        <ValueInputField
          value={opacityPercent}
          onChange={(v) => {
            const num = typeof v === 'string' ? Number.parseInt(v, 10) : v;
            if (!Number.isNaN(num)) {
              const next = Math.max(0, Math.min(100, num)) / 100;
              onOpacityChange(next);
              onOpacityChangeEnd?.(next);
            }
          }}
          isUnit="%"
          min={0}
          max={100}
          step={1}
          defaultValue={100}
          accessibilityLabel={`${accessibilityLabel} opacity`}
          hasMixedValue={isOpacityMixed}
          disabled={isNone}
        />
      </div>
    </div>
  );
};

export default ColorPickerField;
